/* ===========================================================
   music/synth.js — Music Protocol's sound (no files, no libraries:
   the browser's own Web Audio)

   Instruments are made on the spot: tone() for pads/leads/bass,
   kick/snare/hat from noise, rain and thunder for the Weather Radio.
   Clock: a look-ahead scheduler (setInterval asks "what plays in the
   next 120 ms?") so timing stays tight even when the page is busy.
   =========================================================== */

let ctx = null, master = null, verb = null, noiseBuf = null;

export function audio() {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14; comp.ratio.value = 4;
    master = ctx.createGain();
    master.gain.value = 0.7;
    master.connect(comp).connect(ctx.destination);
    // a cheap room: two feedback delays
    verb = ctx.createGain(); verb.gain.value = 0.22;
    for (const [time, fb] of [[0.11, 0.35], [0.17, 0.3]]) {
      const d = ctx.createDelay(1); d.delayTime.value = time;
      const g = ctx.createGain(); g.gain.value = fb;
      const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2400;
      verb.connect(d); d.connect(lp).connect(g).connect(d); lp.connect(master);
    }
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const ch = noiseBuf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) ch[i] = Math.random() * 2 - 1;
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

export const now = () => audio().currentTime;
export const midi = m => 440 * 2 ** ((m - 69) / 12);
export function setVolume(v) { audio(); master.gain.setTargetAtTime(Math.max(0, Math.min(1, v)), ctx.currentTime, 0.05); }

/** One note. */
export function tone(freq, t, dur, { type = 'triangle', gain = 0.18, attack = 0.01, release = 0.25, cutoff = 6000, wet = 0.3, detune = 0 } = {}) {
  audio();
  const o = ctx.createOscillator(); o.type = type; o.frequency.value = freq; o.detune.value = detune;
  const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = cutoff;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + attack);
  g.gain.setValueAtTime(gain, Math.max(t + attack, t + dur - release));
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur + release);
  o.connect(f).connect(g).connect(master);
  if (wet) { const s = ctx.createGain(); s.gain.value = wet; g.connect(s).connect(verb); }
  o.start(t); o.stop(t + dur + release + 0.05);
}

function noise(t, dur, { gain = 0.3, type = 'highpass', freq = 6000, q = 0.7, decay = dur } = {}) {
  audio();
  const s = ctx.createBufferSource(); s.buffer = noiseBuf; s.loop = true;
  const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  s.connect(f).connect(g).connect(master);
  s.start(t, Math.random()); s.stop(t + dur + 0.05);
  return { f, g };
}

export function kick(t, gain = 0.9) {
  audio();
  const o = ctx.createOscillator(); o.type = 'sine';
  const g = ctx.createGain();
  o.frequency.setValueAtTime(150, t); o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
  g.gain.setValueAtTime(gain, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
  o.connect(g).connect(master); o.start(t); o.stop(t + 0.4);
}
export function snare(t, gain = 0.35) {
  noise(t, 0.2, { gain, type: 'bandpass', freq: 1800, q: 0.6, decay: 0.16 });
  tone(190, t, 0.04, { type: 'triangle', gain: gain * 0.5, release: 0.06, wet: 0 });
}
export const hat = (t, gain = 0.12, open = false) => noise(t, open ? 0.3 : 0.06, { gain, freq: 7500, decay: open ? 0.28 : 0.05 });

/** Rain: a steady hiss you can turn up and down. */
export function rainLayer() {
  audio();
  const s = ctx.createBufferSource(); s.buffer = noiseBuf; s.loop = true;
  const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 2500; f.Q.value = 0.4;
  const g = ctx.createGain(); g.gain.value = 0;
  s.connect(f).connect(g).connect(master); s.start();
  return {
    set: v => g.gain.setTargetAtTime(v, ctx.currentTime, 1.5),
    stop: () => { g.gain.setTargetAtTime(0, ctx.currentTime, 0.4); setTimeout(() => { try { s.stop(); } catch { /* stopped */ } }, 1500); },
  };
}
export function thunder(t, gain = 0.5) {
  const { f } = noise(t, 4, { gain, type: 'lowpass', freq: 400, q: 0.3, decay: 3.6 });
  f.frequency.setValueAtTime(900, t); f.frequency.exponentialRampToValueAtTime(90, t + 3);
}

/** Steady clock. onStep(stepIndex, time) is called a little ahead of each step. */
export function clock({ bpm = 100, stepsPerBeat = 4, onStep }) {
  audio();
  let step = 0, next = ctx.currentTime + 0.08, timer = null, tempo = bpm;
  const tick = () => {
    while (next < ctx.currentTime + 0.12) {
      onStep(step, next);
      next += 60 / tempo / stepsPerBeat;
      step++;
    }
  };
  timer = setInterval(tick, 25);
  tick();
  return { setBpm: b => { tempo = Math.max(40, Math.min(220, b)); }, stop: () => clearInterval(timer), get step() { return step; } };
}
